import React, { useState, useEffect, useRef } from 'react'
import { useMutation } from '@tanstack/react-query'
import { renderDefaultEditor } from './Editor'
import AiDraftSuccessScreen from './SuccessScreen'
import AiDraftErrorDisplay from './ErrorDisplay'
import { WorkflowState, WORKFLOW_CONSTANTS, isDraftActionDecided } from '../../../models/aiAnalysis/workflow'
import type { AiDraftEditorProps, AiLegacyDraftWorkflowOrchestratorProps, EditedPayload } from './types'
import { Requests, ReactQuery } from '../../../api'

import { classifyAiActionError, shouldCloseOnDismiss } from '../../../utils/aiAnalysis/actionErrors'
import styles from './styles.module.scss'

// Pre-combine (legacy) forward+acknowledge pair: forced sequential flow —
// forward editor first, a brief "sent" interstitial, then the acknowledge
// editor. Restored as-is for payloads that predate the combine engine.
// Do not extend this component to handle combined action types; those use
// AiDraftWorkflowOrchestrator (co-existence invariant: see Popover.tsx).
const getStateAnnouncement = (state: WorkflowState): string => {
  switch (state) {
    case WorkflowState.FORWARD_DRAFT:
      return 'Review and edit the forward message, then approve or decline.'
    case WorkflowState.FORWARD_SENT:
      return 'Forward message sent successfully. Loading acknowledge message.'
    case WorkflowState.ACKNOWLEDGE_DRAFT:
      return 'Review and edit the acknowledge message, then approve or decline.'
    default:
      return ''
  }
}

const AiLegacyDraftWorkflowOrchestrator: React.FC<AiLegacyDraftWorkflowOrchestratorProps> = ({
  forwardAction,
  acknowledgeAction,
  isOpen,
  onClose,
  onFatalDismiss,
  onWorkflowStateChange,
  autoFocus = true,
  renderEditor
}) => {
  const getInitialWorkflowState = (): WorkflowState | null => {
    const forwardDecided = isDraftActionDecided(forwardAction)
    const acknowledgeDecided = isDraftActionDecided(acknowledgeAction)

    if (forwardDecided && acknowledgeDecided) {
      return null
    } else if (forwardDecided) {
      return WorkflowState.ACKNOWLEDGE_DRAFT
    } else {
      return WorkflowState.FORWARD_DRAFT
    }
  }

  const initialState = getInitialWorkflowState()
  const [workflowState, setWorkflowState] = useState<WorkflowState | null>(initialState)
  const [forwardText, setForwardText] = useState<string>(forwardAction.action_payload.text ?? '')
  const [acknowledgeText, setAcknowledgeText] = useState<string>(acknowledgeAction.action_payload.text ?? '')
  const [retryCount, setRetryCount] = useState<number>(0)
  const [declineError, setDeclineError] = useState<unknown>(null)
  const [announcement, setAnnouncement] = useState<string>('')
  const containerRef = useRef<HTMLDivElement | null>(null)
  const initialFocusDoneRef = useRef<boolean>(false)

  useEffect(() => {
    if (workflowState) {
      setAnnouncement(getStateAnnouncement(workflowState))

      if (onWorkflowStateChange) {
        onWorkflowStateChange(workflowState)
      }

      const isInitialFocus = !initialFocusDoneRef.current
      initialFocusDoneRef.current = true
      if ((autoFocus || !isInitialFocus) && containerRef.current) {
        containerRef.current.focus()
      }
    }
  }, [workflowState, onWorkflowStateChange, autoFocus])

  const approveForwardMutation = useMutation({
    mutationFn: async ({
      actionId,
      decision,
      text
    }: {
      actionId: string
      decision: 'approved' | 'edited'
      text?: string
    }) => {
      const request = Requests.updateAiAction({ actionId, decision, text })
      return await ReactQuery.patchResource(request, request.data)
    },
    onSuccess: () => {
      setRetryCount(0)
      if (isDraftActionDecided(acknowledgeAction)) {
        onClose()
      } else {
        setWorkflowState(WorkflowState.FORWARD_SENT)
      }
    },
    onError: () => {
      setRetryCount((prev) => prev + 1)
    },
    useErrorBoundary: false
  })

  const handleForwardApprove = async (
    actionId: string,
    decision: 'approved' | 'edited',
    editedPayload: EditedPayload | null
  ) => {
    const text = decision === 'edited' ? editedPayload?.text ?? forwardText : undefined

    try {
      await approveForwardMutation.mutateAsync({ actionId, decision, text })
    } catch (error) {
      // Handled by mutation's onError callback
    }
  }

  const approveAcknowledgeMutation = useMutation({
    mutationFn: async ({
      actionId,
      decision,
      text
    }: {
      actionId: string
      decision: 'approved' | 'edited'
      text?: string
    }) => {
      const request = Requests.updateAiAction({ actionId, decision, text })
      return await ReactQuery.patchResource(request, request.data)
    },
    onSuccess: () => {
      setRetryCount(0)
      onClose()
    },
    onError: () => {
      setRetryCount((prev) => prev + 1)
    },
    useErrorBoundary: false
  })

  const handleAcknowledgeApprove = async (
    actionId: string,
    decision: 'approved' | 'edited',
    editedPayload: EditedPayload | null
  ) => {
    const text = decision === 'edited' ? editedPayload?.text ?? acknowledgeText : undefined

    try {
      await approveAcknowledgeMutation.mutateAsync({ actionId, decision, text })
    } catch (error) {
      // Handled by mutation's onError callback
    }
  }

  // Single instance used for all decline calls, including concurrent ones in handleDecline.
  // Errors are caught via try/catch and stored in declineError state — declineActionMutation.error
  // is never read, so concurrent calls clobbering hook state has no effect.
  const declineActionMutation = useMutation({
    mutationFn: async (actionId: string) => {
      const request = Requests.updateAiAction({ actionId, decision: 'declined' })
      return await ReactQuery.patchResource(request, request.data)
    },
    useErrorBoundary: false
  })

  const handleDecline = async () => {
    setDeclineError(null)
    try {
      if (workflowState === WorkflowState.FORWARD_DRAFT) {
        // Decline only the forward — the coordinator must decide the acknowledge separately
        await declineActionMutation.mutateAsync(forwardAction.id)
        if (isDraftActionDecided(acknowledgeAction)) {
          onClose()
        } else {
          setWorkflowState(WorkflowState.ACKNOWLEDGE_DRAFT)
        }
      } else {
        // Forward already approved and executed — only decline acknowledge
        await declineActionMutation.mutateAsync(acknowledgeAction.id)
        onClose()
      }
      return true
    } catch (err) {
      setDeclineError(err)
      setRetryCount((prev) => prev + 1)
      return false
    }
  }

  const currentError =
    declineError ??
    (workflowState === WorkflowState.FORWARD_DRAFT ? approveForwardMutation.error : approveAcknowledgeMutation.error)

  const classifiedError = currentError ? classifyAiActionError(currentError, retryCount) : null

  const handleDismissError = async () => {
    if (classifiedError && shouldCloseOnDismiss(classifiedError.type)) {
      // Dismissing on ALREADY_DECIDED or FATAL leaves any still-pending action
      // undecided — only the explicit Decline button decides an action.
      // Recovery belongs to the host.
      ;(onFatalDismiss ?? onClose)()
    } else {
      setDeclineError(null)
      if (workflowState === WorkflowState.FORWARD_DRAFT) {
        approveForwardMutation.reset()
      } else {
        approveAcknowledgeMutation.reset()
      }
    }
  }

  useEffect(() => {
    if (workflowState === WorkflowState.FORWARD_SENT) {
      const timer = setTimeout(() => {
        setWorkflowState(WorkflowState.ACKNOWLEDGE_DRAFT)
      }, WORKFLOW_CONSTANTS.TRANSITION_DELAY_MS)

      return () => clearTimeout(timer)
    }
    return undefined
  }, [workflowState])

  if (!isOpen || workflowState === null) {
    return null
  }

  const forwardEditorProps: AiDraftEditorProps = {
    action: forwardAction,
    onApprove: handleForwardApprove,
    onDecline: handleDecline,
    currentText: forwardText,
    onTextChange: setForwardText
  }

  const acknowledgeEditorProps: AiDraftEditorProps = {
    action: acknowledgeAction,
    onApprove: handleAcknowledgeApprove,
    onDecline: handleDecline,
    currentText: acknowledgeText,
    onTextChange: setAcknowledgeText
  }

  switch (workflowState) {
    case WorkflowState.FORWARD_DRAFT:
      if (classifiedError) {
        return (
          <div className={styles.workflowContainer} ref={containerRef} tabIndex={-1} aria-label="Error">
            <div className={styles.srOnly} role="status" aria-live="polite" aria-atomic="true">
              {announcement}
            </div>
            <AiDraftErrorDisplay
              error={classifiedError.message}
              errorType={classifiedError.type}
              onDismiss={handleDismissError}
            />
          </div>
        )
      }

      return (
        <div className={styles.workflowContainer} ref={containerRef} tabIndex={-1} aria-label="AI draft workflow">
          <div className={styles.srOnly} role="status" aria-live="polite" aria-atomic="true">
            {announcement}
          </div>
          <React.Fragment key={`${forwardAction.id}-${forwardAction.action_type}`}>
            {(renderEditor ?? renderDefaultEditor)(forwardEditorProps)}
          </React.Fragment>
        </div>
      )

    case WorkflowState.FORWARD_SENT:
      return (
        <div className={styles.workflowContainer} ref={containerRef} tabIndex={-1} aria-label="Success confirmation">
          <div className={styles.srOnly} role="status" aria-live="polite" aria-atomic="true">
            {announcement}
          </div>
          <AiDraftSuccessScreen
            message={WORKFLOW_CONSTANTS.SUCCESS_MESSAGE_FORWARD}
            subtitle={WORKFLOW_CONSTANTS.SUCCESS_SUBTITLE_FORWARD}
            isLoading
          />
        </div>
      )

    case WorkflowState.ACKNOWLEDGE_DRAFT:
      if (classifiedError) {
        return (
          <div className={styles.workflowContainer} ref={containerRef} tabIndex={-1} aria-label="Error">
            <div className={styles.srOnly} role="status" aria-live="polite" aria-atomic="true">
              {announcement}
            </div>
            <AiDraftErrorDisplay
              error={classifiedError.message}
              errorType={classifiedError.type}
              onDismiss={handleDismissError}
            />
          </div>
        )
      }

      return (
        <div className={styles.workflowContainer} ref={containerRef} tabIndex={-1} aria-label="AI draft workflow">
          <div className={styles.srOnly} role="status" aria-live="polite" aria-atomic="true">
            {announcement}
          </div>
          <React.Fragment key={`${acknowledgeAction.id}-${acknowledgeAction.action_type}`}>
            {(renderEditor ?? renderDefaultEditor)(acknowledgeEditorProps)}
          </React.Fragment>
        </div>
      )

    default:
      return null
  }
}

export default AiLegacyDraftWorkflowOrchestrator
